// src/pages/TradingDashboard.js
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

import {
  LogOut,
  Wallet,
  TrendingUp,
  TrendingDown,
  DollarSign,
  ArrowDownToLine,
  Menu,
  MessageCircle,
  X,
  Send,
  Bot,
  UserRound,
  BellRing,
  CheckCircle2,
} from "lucide-react";

import DepositModal from "../components/DepositModal";
import WithdrawalModal from "../components/WithdrawalModal";
import OrdersTable from "../components/OrdersTable";

const DEFAULT_KC_SYMBOL = "KCK6";
const LBS_PER_60KG_SACK = 132.2772;

// carimbo pra conferir se esta versão está na tela
const BUILD_TAG = "TD_TRADER_MOBILE_PRO_2026-08-15_01";

// ===== helpers =====
function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0,00";
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatBrl(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatQuote(value, digits = 2) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "--";
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function moneyMaskFromDigits(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return { display: "", numeric: "" };
  const cents = Number(digits);
  const amount = cents / 100;
  return { display: formatBrl(amount), numeric: String(amount) };
}
function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}
function readTokenFromStorage() {
  return (
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("auth_token") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("jwt") ||
    ""
  );
}
function normalizeBearer(token) {
  const t = String(token || "").trim();
  if (!t) return "";
  return t.toLowerCase().startsWith("bearer ") ? t : `Bearer ${t}`;
}
function applyAxiosAuthHeader(tokenLike) {
  const token = String(tokenLike || "").trim();
  if (token) {
    axios.defaults.headers.common.Authorization = normalizeBearer(token);
  } else {
    delete axios.defaults.headers.common.Authorization;
  }
}
async function safeGet(url, options = {}) {
  const res = await axios.get(url, {
    timeout: 2500,
    validateStatus: () => true,
    ...options,
  });
  return res;
}
async function probeApiBase(baseUrl) {
  const endpoints = ["/ping", "/health", "/status"];
  for (const ep of endpoints) {
    try {
      const r = await safeGet(`${baseUrl}${ep}`);
      if (r?.status === 200) return true;
    } catch (_) {}
  }
  return false;
}
function buildOriginCandidates() {
  const origin = window.location.origin;
  const u = new URL(origin);
  const host = u.hostname;

  const candidates = new Set([origin]);

  if (host.startsWith("www.")) {
    candidates.add(`${u.protocol}//${host.replace(/^www\./, "")}`);
  } else {
    candidates.add(`${u.protocol}//www.${host}`);
  }

  // reforço
  candidates.add(`${u.protocol}//cofy.app`);
  candidates.add(`${u.protocol}//www.cofy.app`);

  return Array.from(candidates);
}
function join(base, path) {
  const b = String(base || "").replace(/\/+$/, "");
  return `${b}${path}`;
}

// ✅ axios SEM Authorization para cotações (evita CORS/preflight quando muda www <-> sem www)
const quotesAxios = axios.create({
  timeout: 5000,
  headers: { "Content-Type": "application/json" },
  withCredentials: false,
});

// tenta achar uma base que funcione pros DOIS arquivos (kc e dol)
async function resolveQuotesBase() {
  const origin = window.location.origin;

  // ordem: primeiro same-origin (local/prod), depois fallbacks absolutos
  const bases = [
    `${origin}/data`,
    `${origin}/api/data`,
    "https://www.cofy.app/api/data",
    "https://cofy.app/api/data",
  ];

  for (const base of bases) {
    try {
      const [kc, dol] = await Promise.all([
        quotesAxios.get(join(base, "/oncafe_kc.json"), { validateStatus: () => true }),
        quotesAxios.get(join(base, "/oncafe_dol.json"), { validateStatus: () => true }),
      ]);

      if (kc?.status !== 200 || dol?.status !== 200) continue;

      const kcPrice = Number(kc?.data?.price);
      const dolPrice = Number(dol?.data?.price);

      if (Number.isFinite(kcPrice) && kcPrice > 0 && Number.isFinite(dolPrice) && dolPrice > 0) {
        return base; // ✅ achou
      }
    } catch (_) {
      // continua tentando
    }
  }

  return ""; // não achou
}

export default function TradingDashboard({ user, onLogout }) {
  // ===== estado =====
  const [balance, setBalance] = useState(0);
  const [totalCofyQty, setTotalCofyQty] = useState(0);

  const [marketData, setMarketData] = useState(null);
  const [orders, setOrders] = useState([]);

  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdrawal, setShowWithdrawal] = useState(false);

  // ===== Cadastro para saque =====
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [withdrawProfileCompleted, setWithdrawProfileCompleted] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    cpf: "",
    phone: "",
    birth_date: "",
    city: "",
    state: "",
    pix_key_type: "cpf",
    pix_key: "",
  });

  const [userSettings, setUserSettings] = useState({
    min_close_time_minutes: 1440,
    spread_percentage: 0.2,
  });

  const [tradeSide, setTradeSide] = useState("buy");
  const [tradeAmount, setTradeAmount] = useState("");
  const [tradeError, setTradeError] = useState("");
  const [buyLoading, setBuyLoading] = useState(false);
  const [sellLoading, setSellLoading] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [pendingTradeConfirm, setPendingTradeConfirm] = useState(null);
  const [lastQuoteUpdateAt, setLastQuoteUpdateAt] = useState(0);

  // ===== Avisos enviados pelo Admin =====
  const [userNotices, setUserNotices] = useState([]);
  const [noticeLoading, setNoticeLoading] = useState(false);
  const [noticeConfirming, setNoticeConfirming] = useState(false);

  // ===== Conta Parceira =====
  const [partnerData, setPartnerData] = useState(null);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [showPartnerPanel, setShowPartnerPanel] = useState(false);
  const [newPartnerCommissions, setNewPartnerCommissions] = useState(0);

  // ===== Assistente COFY (isolado das operações do Trader) =====
  const [showAssistant, setShowAssistant] = useState(false);
  const [assistantMessage, setAssistantMessage] = useState("");
  const [assistantLoading, setAssistantLoading] = useState(false);
  const [assistantError, setAssistantError] = useState("");
  const [assistantMessages, setAssistantMessages] = useState([
    {
      role: "assistant",
      content:
        "Olá! Sou o Assistente COFY. Posso ajudar com dúvidas sobre a plataforma, Bolsa de NY, dólar, preço da saca e funcionamento das operações.",
    },
  ]);
  const assistantEndRef = useRef(null);

  useEffect(() => {
    if (!showAssistant) return;
    const timer = setTimeout(() => {
      assistantEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 80);
    return () => clearTimeout(timer);
  }, [showAssistant, assistantMessages, assistantLoading]);

  // ✅ cotações do TRADER (RAW)
  const [kcData, setKcData] = useState(null); // {symbol, price(raw), change_pct,...}
  const [kcDisplayCents, setKcDisplayCents] = useState(0);
  const [usdBrl, setUsdBrl] = useState(0);
  const [selectedKcSymbol, setSelectedKcSymbol] = useState(DEFAULT_KC_SYMBOL);

  const [apiBase, setApiBase] = useState(localStorage.getItem("api_base_used") || "");
  const [quotesBase, setQuotesBase] = useState(localStorage.getItem("quotes_base_used") || "");

  const lastGoodUsdRef = useRef(0);
  const lastGoodKcRef = useRef(null);
  const lastGoodKcDisplayRef = useRef(0);

  const lastQuotesBaseRef = useRef("");
  const lastWarnAtRef = useRef(0);

  // ===== animações leves (flash) =====
  const prevKcRef = useRef(null);
  const prevUsdRef = useRef(null);
  const [kcFlash, setKcFlash] = useState(null); // "up" | "down" | null
  const [usdFlash, setUsdFlash] = useState(null); // "up" | "down" | null

  useEffect(() => {
    if (!(kcDisplayCents > 0)) return;

    const curr = Number(kcDisplayCents);
    const prev = prevKcRef.current;

    if (Number.isFinite(prev) && prev > 0 && curr !== prev) {
      setKcFlash(curr > prev ? "up" : "down");
      const t = setTimeout(() => setKcFlash(null), 700);
      prevKcRef.current = curr;
      return () => clearTimeout(t);
    }

    prevKcRef.current = curr;
  }, [kcDisplayCents]);

  useEffect(() => {
    if (!(usdBrl > 0)) return;

    const curr = Number(usdBrl);
    const prev = prevUsdRef.current;

    if (Number.isFinite(prev) && prev > 0 && curr !== prev) {
      setUsdFlash(curr > prev ? "up" : "down");
      const t = setTimeout(() => setUsdFlash(null), 700);
      prevUsdRef.current = curr;
      return () => clearTimeout(t);
    }

    prevUsdRef.current = curr;
  }, [usdBrl]);

  // ===== AUTH =====
  useEffect(() => {
    const tokenFromUser = user?.access_token || user?.token || user?.jwt || "";
    const token = tokenFromUser || readTokenFromStorage();
    applyAxiosAuthHeader(token);
  }, [user]);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const status = err?.response?.status;
        const url = err?.config?.url || "";
        const isProbe =
          url.includes("/health") ||
          url.includes("/status") ||
          url.includes("/ping");

        if (status === 401 && !isProbe) {
          if (typeof onLogout === "function") onLogout();
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [onLogout]);

  // ===== RESOLVE API BASE =====
  const resolveApiBase = useCallback(async () => {
    const origins = buildOriginCandidates();

    const apiCandidates = [];
    for (const o of origins) {
      apiCandidates.push(`${o}/api/trader`);
      apiCandidates.push(`${o}/api/trader/index.php`);
    }

    const savedApi = localStorage.getItem("api_base_used");
    if (savedApi) apiCandidates.unshift(savedApi);

    let foundApi = "";
    for (const c of apiCandidates) {
      const ok = await probeApiBase(c);
      if (ok) {
        foundApi = c;
        break;
      }
    }

    if (foundApi) {
      localStorage.setItem("api_base_used", foundApi);
      setApiBase(foundApi);
    }
  }, []);

  useEffect(() => {
    resolveApiBase();
  }, [resolveApiBase]);

  // ===== FETCHERS (API trader) =====
  const fetchBalance = useCallback(async () => {
    if (!apiBase) return;
    try {
      const r = await axios.get(`${apiBase}/user/balance`, { timeout: 5000 });
      const data = r?.data ?? {};

      const bal = Number(data.balance ?? data.balance_brl ?? data.data?.balance ?? data.data?.balance_brl);
      const qty = Number(
        data.total_cofy_qty ??
          data.total_lots ??
          data.total_qty ??
          data.lots ??
          data.cofy_qty ??
          data.data?.total_cofy_qty ??
          data.data?.total_lots
      );

      setBalance(Number.isFinite(bal) ? bal : 0);
      setTotalCofyQty(Number.isFinite(qty) ? qty : 0);
    } catch (e) {}
  }, [apiBase]);

  const fetchUserProfile = useCallback(async () => {
    if (!apiBase) return;

    setProfileLoading(true);
    try {
      const r = await axios.get(`${apiBase}/user/profile`, { timeout: 5000 });
      const data = r?.data ?? {};
      const minutes = Number(data.min_close_time_minutes ?? data.data?.min_close_time_minutes);
      const spread = Number(data.spread_percentage ?? data.data?.spread_percentage);

      setUserSettings({
        min_close_time_minutes: Number.isFinite(minutes) ? minutes : 1440,
        spread_percentage: Number.isFinite(spread) ? spread : 0.2,
      });

      setWithdrawProfileCompleted(Boolean(data.withdraw_profile_completed));

      setProfileData((prev) => ({
        ...prev,
        name: data.name ?? prev.name ?? user?.name ?? "",
        email: data.email ?? prev.email ?? user?.email ?? "",
        cpf: data.cpf ?? "",
        phone: data.phone ?? "",
        birth_date: data.birth_date ?? "",
        city: data.city ?? "",
        state: data.state ?? "",
        pix_key_type: data.pix_key_type ?? "cpf",
        pix_key: data.pix_key ?? "",
      }));

      return data;
    } catch (e) {
      return null;
    } finally {
      setProfileLoading(false);
    }
  }, [apiBase, user]);

  const fetchUserNotices = useCallback(async () => {
    if (!apiBase) return;

    setNoticeLoading(true);
    try {
      const r = await axios.get(`${apiBase}/user/messages`, { timeout: 5000 });
      const rows = Array.isArray(r?.data?.rows) ? r.data.rows : [];
      setUserNotices(rows);
    } catch (e) {
      // Aviso não deve impedir o uso do Trader se houver falha temporária.
      console.error("[TRADER NOTICE] erro ao carregar:", e?.response?.data || e);
    } finally {
      setNoticeLoading(false);
    }
  }, [apiBase]);

  const handleNoticeRead = async () => {
    const current = userNotices[0];
    if (!current?.id || !apiBase || noticeConfirming) return;

    setNoticeConfirming(true);
    try {
      await axios.post(
        `${apiBase}/user/messages/${current.id}/read`,
        {},
        { timeout: 5000 }
      );

      // Remove imediatamente da tela.
      setUserNotices((prev) => prev.filter((item) => item.id !== current.id));
    } catch (e) {
      console.error("[TRADER NOTICE] erro ao confirmar leitura:", e?.response?.data || e);
    } finally {
      setNoticeConfirming(false);
    }
  };

  const fetchPartnerData = useCallback(async () => {
    if (!apiBase) return;

    setPartnerLoading(true);
    try {
      const r = await axios.get(`${apiBase}/partner/me`, {
        timeout: 5000,
        validateStatus: () => true,
      });

      // Conta comum não deve sofrer nenhum impacto.
      if (r?.status === 200 && r?.data?.is_partner === true) {
        const data = r.data;
        setPartnerData(data);

        // Badge de novas comissões.
        // Na primeira vez, considera o histórico atual como já visto para não
        // mostrar dezenas de notificações antigas.
        const partnerId = Number(data?.partner?.id || 0);
        const commissions = Array.isArray(data?.commissions) ? data.commissions : [];
        const maxCommissionId = commissions.reduce(
          (max, item) => Math.max(max, Number(item?.id || 0)),
          0
        );

        if (partnerId > 0) {
          const storageKey = `cofy_partner_seen_commission_${partnerId}`;
          const stored = localStorage.getItem(storageKey);

          if (stored === null) {
            localStorage.setItem(storageKey, String(maxCommissionId));
            setNewPartnerCommissions(0);
          } else {
            const lastSeenId = Number(stored || 0);
            const unread = commissions.filter(
              (item) => Number(item?.id || 0) > lastSeenId
            ).length;
            setNewPartnerCommissions(unread);
          }
        }
      } else {
        setPartnerData(null);
        setNewPartnerCommissions(0);
      }
    } catch (e) {
      console.error("[PARTNER] erro ao carregar:", e?.response?.data || e);
      setPartnerData(null);
    } finally {
      setPartnerLoading(false);
    }
  }, [apiBase]);

  const fetchOrders = useCallback(async () => {
    if (!apiBase) return;
    try {
      const r = await axios.get(`${apiBase}/trading/orders`, { timeout: 5000 });
      const data = r?.data ?? {};

      const list = Array.isArray(data)
        ? data
        : Array.isArray(data.orders)
        ? data.orders
        : Array.isArray(data.data?.orders)
        ? data.data.orders
        : [];

      setOrders(list);
    } catch (e) {}
  }, [apiBase]);

  // ===== FETCH TRADER QUOTES (RAW) =====
  const fetchTraderQuotes = useCallback(async () => {
    try {
      let base = quotesBase || "";

      // resolve base se ainda não tem ou se mudou ambiente
      if (!base) {
        base = await resolveQuotesBase();
        if (base) {
          localStorage.setItem("quotes_base_used", base);
          setQuotesBase(base);
        }
      }

      if (!base) {
        // warn (1x a cada 5s)
        const now = Date.now();
        if (now - lastWarnAtRef.current > 5000) {
          lastWarnAtRef.current = now;
          console.warn("[TRADER QUOTES] não achei base válida. Verifique /data ou /api/data no mesmo domínio.");
        }
        return;
      }

      const [kcResp, dolResp] = await Promise.all([
        quotesAxios.get(join(base, "/oncafe_kc.json"), { validateStatus: () => true }),
        quotesAxios.get(join(base, "/oncafe_dol.json"), { validateStatus: () => true }),
      ]);

      if (kcResp?.status !== 200 || dolResp?.status !== 200) {
        // força re-resolve na próxima volta
        localStorage.removeItem("quotes_base_used");
        setQuotesBase("");
        return;
      }

      const kcRaw = kcResp.data || null;
      const dolRaw = dolResp.data || null;
      setLastQuoteUpdateAt(Date.now());

      // ----- dólar RAW -----
      const dollar =
        dolRaw && typeof dolRaw === "object"
          ? toNumber(dolRaw.price ?? dolRaw.ask ?? dolRaw.bid, 0)
          : 0;

      if (dollar > 0 && dollar < 20) {
        lastGoodUsdRef.current = dollar;
        setUsdBrl(dollar);
      } else if (lastGoodUsdRef.current > 0) {
        setUsdBrl((prev) => prev || lastGoodUsdRef.current);
      }

      // ----- KC RAW (IGNORA price_adjusted) -----
      if (kcRaw && typeof kcRaw === "object") {
        const sym = String(kcRaw.symbol || DEFAULT_KC_SYMBOL);
        const kcCents = toNumber(kcRaw.price, 0); // ✅ só raw

        if (kcCents > 0) {
          setSelectedKcSymbol(sym);

          lastGoodKcDisplayRef.current = kcCents;
          setKcDisplayCents(kcCents);

          const payload = {
            symbol: sym,
            price: kcCents,
            change_pct: toNumber(kcRaw.change_pct ?? kcRaw.changePct, 0),
            change_abs: toNumber(kcRaw.change_abs ?? kcRaw.changeAbs, 0),
            bid: toNumber(kcRaw.bid, 0),
            ask: toNumber(kcRaw.ask, 0),
            open: toNumber(kcRaw.open ?? kcRaw.abertura, 0),
            high: toNumber(kcRaw.high ?? kcRaw.max ?? kcRaw.maxima ?? kcRaw.maximum, 0),
            low: toNumber(kcRaw.low ?? kcRaw.min ?? kcRaw.minima ?? kcRaw.minimum, 0),
            close: toNumber(
              kcRaw.close ??
                kcRaw.previous_close ??
                kcRaw.prev_close ??
                kcRaw.fechamento ??
                kcRaw.settle ??
                kcRaw.settlement,
              0
            ),
          };

          lastGoodKcRef.current = payload;
          setKcData(payload);
        } else if (lastGoodKcRef.current) {
          setKcData((prev) => prev || lastGoodKcRef.current);
        }
      } else if (lastGoodKcRef.current) {
        setKcData((prev) => prev || lastGoodKcRef.current);
      }

      if (kcDisplayCents <= 0 && lastGoodKcDisplayRef.current > 0) {
        setKcDisplayCents((prev) => prev || lastGoodKcDisplayRef.current);
      }

      // log 1x quando mudar a base
      if (base && lastQuotesBaseRef.current !== base) {
        lastQuotesBaseRef.current = base;
        console.log("[TRADER QUOTES] base:", base);
      }

      setMarketData((prev) => ({
        ...(prev || {}),
        symbol: "SACA_CAFE_BRL",
        raw: { kc: kcRaw, dol: dolRaw, base },
      }));
    } catch (e) {
      // força re-resolve
      localStorage.removeItem("quotes_base_used");
      setQuotesBase("");
    }
  }, [quotesBase, kcDisplayCents]);

  // ✅ cotações SEMPRE (não dependem do apiBase)
  useEffect(() => {
    fetchTraderQuotes();
    const id = setInterval(fetchTraderQuotes, 1000);
    return () => clearInterval(id);
  }, [fetchTraderQuotes]);

  // ✅ operações dependem do apiBase
  useEffect(() => {
    if (!apiBase) return;

    fetchBalance();
    fetchUserProfile();
    fetchOrders();
    fetchUserNotices();
    fetchPartnerData();

    const ordersInterval = setInterval(fetchOrders, 1000);
    const balanceInterval = setInterval(fetchBalance, 5000);
    const noticesInterval = setInterval(fetchUserNotices, 30000);
    const partnerInterval = setInterval(fetchPartnerData, 30000);

    return () => {
      clearInterval(ordersInterval);
      clearInterval(balanceInterval);
      clearInterval(noticesInterval);
      clearInterval(partnerInterval);
    };
  }, [apiBase, fetchBalance, fetchUserProfile, fetchOrders, fetchUserNotices, fetchPartnerData]);

  // ===== derived =====
  const safeOrders = Array.isArray(orders) ? orders : [];
  const openOrders = safeOrders.filter((o) => String(o?.status || "").toLowerCase() === "open");
  const closedOrders = safeOrders.filter((o) => String(o?.status || "").toLowerCase() === "closed");

  const totalProfit = closedOrders.reduce((sum, o) => {
    const v = Number(o?.profit_loss ?? o?.profit_loss_brl ?? o?.profit ?? o?.pnl);
    return sum + (Number.isFinite(v) ? v : 0);
  }, 0);

  const spreadPct = toNumber(userSettings?.spread_percentage, 0.2);
  const effectiveUsdBrl = usdBrl > 0 ? usdBrl : 0;

  // ✅ saca = KC raw + dólar raw
  const fairPrice = useMemo(() => {
    const kcCentsPerLb = toNumber(kcDisplayCents > 0 ? kcDisplayCents : kcData?.price, 0);
    const dollar = toNumber(effectiveUsdBrl, 0);
    if (kcCentsPerLb <= 0 || dollar <= 0) return 0;

    const usdPerLb = kcCentsPerLb / 100;
    const sackBrl = usdPerLb * LBS_PER_60KG_SACK * dollar;
    return Number.isFinite(sackBrl) ? sackBrl : 0;
  }, [kcDisplayCents, kcData, effectiveUsdBrl]);

  const buyPrice = fairPrice > 0 ? fairPrice * (1 + spreadPct / 100) : 0;
  const sellPrice = fairPrice > 0 ? fairPrice * (1 - spreadPct / 100) : 0;

  const tradeAmountNum = Number(tradeAmount) || 0;
  const estimatedBuyCofy = buyPrice > 0 && tradeAmountNum > 0 ? tradeAmountNum / buyPrice : 0;
  const estimatedSellCofy = sellPrice > 0 && tradeAmountNum > 0 ? tradeAmountNum / sellPrice : 0;

  const positionLots = useMemo(() => (totalCofyQty > 0 ? totalCofyQty : 0), [totalCofyQty]);
  const positionSacks = useMemo(() => positionLots, [positionLots]); // 1 lote = 1 saca

  const positionValueBrl = useMemo(() => {
    if (fairPrice <= 0 || positionSacks <= 0) return 0;
    return positionSacks * fairPrice;
  }, [positionSacks, fairPrice]);

  const cashSacksAtBuy = useMemo(() => {
    if (buyPrice <= 0 || balance <= 0) return 0;
    return balance / buyPrice;
  }, [balance, buyPrice]);

  const portfolioTotalBrl = useMemo(() => {
    const b = Number.isFinite(Number(balance)) ? Number(balance) : 0;
    const p = Number.isFinite(Number(positionValueBrl)) ? Number(positionValueBrl) : 0;
    return Math.max(0, b) + Math.max(0, p);
  }, [balance, positionValueBrl]);

  const portfolioSacksAtBuy = useMemo(() => {
    if (buyPrice <= 0 || portfolioTotalBrl <= 0) return 0;
    return portfolioTotalBrl / buyPrice;
  }, [portfolioTotalBrl, buyPrice]);

  const userSettingsNoMinClose = { ...userSettings, min_close_time_minutes: 0 };

  const marketDataForOrders = useMemo(() => {
    if (fairPrice <= 0) return marketData;
    return {
      ...(marketData || {}),
      symbol: "SACA_CAFE_BRL",
      price: fairPrice,
      currentPrice: fairPrice,
      current_price: fairPrice,
      open: fairPrice,
      high: fairPrice,
      low: fairPrice,
      change_pct: toNumber(kcData?.change_pct, marketData?.change_pct ?? 0),
      change_abs: toNumber(kcData?.change_abs, marketData?.change_abs ?? 0),
    };
  }, [marketData, fairPrice, kcData]);

  // ===== actions =====
  const handleOrderPlaced = () => {
    fetchBalance();
    fetchOrders();
  };

  const handleBuy = async () => {
    const amountNum = Number(tradeAmount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setTradeError("Informe um valor em reais para abrir operação de COMPRA.");
      return;
    }
    if (fairPrice <= 0) {
      setTradeError("Aguardando cotação da bolsa + dólar para calcular o preço.");
      return;
    }
    if (amountNum > balance + 0.00001) {
      setTradeError("Saldo em reais insuficiente para abrir essa operação.");
      return;
    }

    setBuyLoading(true);
    setTradeError("");
    try {
      await axios.post(`${apiBase}/trading/orders`, { side: "buy", amount: amountNum });
      handleOrderPlaced();
      setTradeAmount("");
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.response?.data?.error || "Erro ao abrir operação de compra.";
      setTradeError(msg);
    } finally {
      setBuyLoading(false);
    }
  };

  const handleSell = async () => {
    const amountNum = Number(tradeAmount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setTradeError("Informe um valor em reais para abrir operação de VENDA.");
      return;
    }
    if (fairPrice <= 0) {
      setTradeError("Aguardando cotação da bolsa + dólar para calcular o preço.");
      return;
    }
    if (amountNum > balance + 0.00001) {
      setTradeError("Saldo em reais insuficiente para abrir essa operação.");
      return;
    }

    setSellLoading(true);
    setTradeError("");
    try {
      await axios.post(`${apiBase}/trading/orders`, { side: "sell", amount: amountNum });
      handleOrderPlaced();
      setTradeAmount("");
    } catch (error) {
      const msg = error?.response?.data?.detail || error?.response?.data?.error || "Erro ao abrir operação de venda.";
      setTradeError(msg);
    } finally {
      setSellLoading(false);
    }
  };

  const formatCpfInput = (value) => {
    const digits = String(value || "").replace(/\D/g, "").slice(0, 11);
    return digits
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/\.(\d{3})(\d)/, ".$1-$2");
  };

  const formatPhoneInput = (value) => {
    const digits = String(value || "").replace(/\D/g, "").slice(0, 11);

    if (digits.length <= 10) {
      return digits
        .replace(/^(\d{2})(\d)/, "($1) $2")
        .replace(/(\d{4})(\d)/, "$1-$2");
    }

    return digits
      .replace(/^(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{5})(\d)/, "$1-$2");
  };

  const handleOpenWithdrawal = async () => {
    setShowMenu(false);
    setProfileError("");

    const profile = await fetchUserProfile();

    if (profile?.withdraw_profile_completed) {
      setShowWithdrawal(true);
      return;
    }

    setShowProfileModal(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();

    if (!apiBase || profileSaving) return;

    setProfileSaving(true);
    setProfileError("");

    try {
      const payload = {
        name: String(profileData.name || "").trim(),
        cpf: String(profileData.cpf || ""),
        phone: String(profileData.phone || ""),
        birth_date: String(profileData.birth_date || ""),
        city: String(profileData.city || "").trim(),
        state: String(profileData.state || "").trim().toUpperCase(),
        pix_key_type: String(profileData.pix_key_type || "cpf"),
        pix_key:
          profileData.pix_key_type === "cpf"
            ? String(profileData.cpf || "")
            : String(profileData.pix_key || "").trim(),
      };

      const r = await axios.put(`${apiBase}/user/profile`, payload, { timeout: 12000 });
      const data = r?.data ?? {};

      if (!data?.ok) {
        throw new Error(data?.detail || "Não foi possível salvar o cadastro.");
      }

      setWithdrawProfileCompleted(true);
      setShowProfileModal(false);

      await fetchUserProfile();

      // Depois de completar o cadastro, continua exatamente para o saque.
      setShowWithdrawal(true);
    } catch (error) {
      setProfileError(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          error?.message ||
          "Não foi possível salvar o cadastro."
      );
    } finally {
      setProfileSaving(false);
    }
  };

  const handleDepositSuccess = () => {
    setShowDeposit(false);
    fetchBalance();
  };

  const handleWithdrawalSuccess = () => {
    setShowWithdrawal(false);
    fetchBalance();
  };

  const handleOrderClosed = () => {
    fetchBalance();
    fetchOrders();
  };

  // ===== Assistente COFY =====
  const handleAssistantSend = async (forcedMessage = "") => {
    const message = String(typeof forcedMessage === "string" && forcedMessage ? forcedMessage : assistantMessage || "").trim();
    if (!message || assistantLoading) return;

    setAssistantError("");
    setAssistantMessage("");
    setAssistantMessages((prev) => [...prev, { role: "user", content: message }]);
    setAssistantLoading(true);

    try {
      const response = await axios.post(
        `${window.location.origin}/api/assistant_chat.php`,
        {
          message,
          context: {
            area: "trader",
            kc_symbol: selectedKcSymbol,
            kc_cents_lb: (kcDisplayCents > 0 ? kcDisplayCents : toNumber(kcData?.price, 0)) || null,
            usd_brl: effectiveUsdBrl > 0 ? effectiveUsdBrl : null,
            sack_brl: fairPrice > 0 ? fairPrice : null,
          },
        },
        {
          timeout: 30000,
          validateStatus: () => true,
          headers: {
            Authorization: normalizeBearer(
              user?.access_token ||
              user?.token ||
              user?.jwt ||
              readTokenFromStorage()
            ),
          },
        }
      );

      const data = response?.data ?? {};
      const reply =
        typeof data === "string"
          ? data
          : data.reply ?? data.message ?? data.answer ?? data.output_text ?? "";

      if (response?.status >= 200 && response?.status < 300 && String(reply || "").trim()) {
        setAssistantMessages((prev) => [
          ...prev,
          { role: "assistant", content: String(reply).trim() },
        ]);
      } else {
        throw new Error(
          data?.error || data?.detail || "Não foi possível obter uma resposta do assistente."
        );
      }
    } catch (error) {
      setAssistantError(
        error?.message || "Não foi possível falar com o Assistente COFY agora."
      );
    } finally {
      setAssistantLoading(false);
    }
  };

  const partner = partnerData?.partner ?? partnerData ?? {};
  const partnerSummary = partnerData?.summary ?? partnerData?.totals ?? partnerData ?? {};
  const partnerReferrals = Array.isArray(partnerData?.referrals)
    ? partnerData.referrals
    : Array.isArray(partnerData?.referred_users)
    ? partnerData.referred_users
    : [];
  const partnerCommissions = Array.isArray(partnerData?.commissions)
    ? partnerData.commissions
    : Array.isArray(partnerData?.history)
    ? partnerData.history
    : [];

  const partnerCode = partner.referral_code ?? partnerData?.referral_code ?? "";
  const partnerCommissionPct = toNumber(partner.commission_pct ?? partnerData?.commission_pct, 0);
  const partnerReferralCount = toNumber(
    partnerSummary.referrals_count ?? partnerSummary.referred_count ?? partnerData?.referrals_count,
    partnerReferrals.length
  );
  const partnerAvailable = toNumber(
    partnerSummary.available ?? partnerSummary.available_amount ?? partnerSummary.commission_available,
    0
  );
  const partnerPaid = toNumber(partnerSummary.paid ?? partnerSummary.paid_amount ?? partnerSummary.commission_paid, 0);
  const partnerPending = toNumber(
    partnerSummary.pending ?? partnerSummary.pending_amount ?? partnerSummary.commission_pending,
    0
  );
  const partnerTotalCommission = toNumber(
    partnerSummary.total_commission ?? partnerSummary.commission_total ?? partnerSummary.total,
    partnerAvailable + partnerPaid + partnerPending
  );
  const partnerSpreadGenerated = toNumber(
    partnerSummary.spread_total ?? partnerSummary.total_spread ?? partnerSummary.spread_generated,
    0
  );

  const partnerReferralLink = partnerCode
    ? `${window.location.origin}/trader?ref=${encodeURIComponent(partnerCode)}`
    : "";

  const handleShareReferral = async () => {
    if (!partnerReferralLink) return;

    const sharePayload = {
      title: "COFY Trader",
      text: "Crie sua conta no COFY pelo meu convite.",
      url: partnerReferralLink,
    };

    try {
      if (navigator.share) {
        await navigator.share(sharePayload);
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(partnerReferralLink);
        alert("Link de indicação copiado.");
        return;
      }

      window.prompt("Copie seu link de indicação:", partnerReferralLink);
    } catch (e) {
      // Cancelar o compartilhamento não é erro para o usuário.
      if (e?.name !== "AbortError") {
        window.prompt("Copie seu link de indicação:", partnerReferralLink);
      }
    }
  };

  const openPartnerPanel = () => {
    const partnerId = Number(partner?.id || 0);
    const maxCommissionId = partnerCommissions.reduce(
      (max, item) => Math.max(max, Number(item?.id || 0)),
      0
    );

    if (partnerId > 0) {
      localStorage.setItem(
        `cofy_partner_seen_commission_${partnerId}`,
        String(maxCommissionId)
      );
    }

    setNewPartnerCommissions(0);
    setShowPartnerPanel(true);
  };

  // ===== UI =====
  const currentNotice = userNotices.length > 0 ? userNotices[0] : null;

  const kcDisplay = kcDisplayCents > 0 ? kcDisplayCents : toNumber(kcData?.price, 0);
  const kcChangePct = toNumber(kcData?.change_pct, 0);
  const kcChangeAbs = toNumber(kcData?.change_abs, 0);
  const hasPosition = positionSacks > 0.000001;
  const selectedEntryPrice = tradeSide === "buy" ? buyPrice : sellPrice;
  const selectedEstimatedQty = tradeSide === "buy" ? estimatedBuyCofy : estimatedSellCofy;
  const tradeAmountDisplay = tradeAmountNum > 0 ? formatBrl(tradeAmountNum) : "";

  const setQuickTradeAmount = (amount) => {
    const safe = Math.max(0, Math.min(Number(amount) || 0, Number(balance) || 0));
    setTradeAmount(safe > 0 ? safe.toFixed(2) : "");
    setTradeError("");
  };

  const handleTradeAmountMasked = (rawValue) => {
    const masked = moneyMaskFromDigits(rawValue);
    setTradeAmount(masked.numeric);
    setTradeError("");
  };

  const requestTradeConfirmation = (side) => {
    const amountNum = Number(tradeAmount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) { setTradeError("Informe um valor em reais para abrir a operação."); return; }
    if (fairPrice <= 0) { setTradeError("Aguardando cotação da bolsa + dólar para calcular o preço."); return; }
    if (amountNum > balance + 0.00001) { setTradeError("Saldo em reais insuficiente para abrir essa operação."); return; }
    setTradeSide(side);
    setTradeError("");
    setPendingTradeConfirm({ side, amount: amountNum });
  };

  const quoteIsLive = lastQuoteUpdateAt > 0 && Date.now() - lastQuoteUpdateAt < 10000;
  const quoteTimeLabel = lastQuoteUpdateAt ? new Date(lastQuoteUpdateAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "--:--:--";

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="trading-dashboard">
      {/* ===== Aviso enviado pelo Admin ===== */}
      {currentNotice && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4">
          <div
            className={[
              "w-full max-w-md rounded-2xl border bg-slate-950 shadow-2xl overflow-hidden",
              currentNotice.priority === "important"
                ? "border-amber-500/40"
                : "border-emerald-500/30",
            ].join(" ")}
          >
            <div
              className={[
                "px-4 py-3 border-b flex items-center gap-3",
                currentNotice.priority === "important"
                  ? "border-amber-500/20 bg-amber-500/10"
                  : "border-emerald-500/20 bg-emerald-500/10",
              ].join(" ")}
            >
              <div
                className={[
                  "w-9 h-9 rounded-xl flex items-center justify-center",
                  currentNotice.priority === "important"
                    ? "bg-amber-400/20 text-amber-300"
                    : "bg-emerald-400/20 text-emerald-300",
                ].join(" ")}
              >
                <BellRing className="w-5 h-5" />
              </div>

              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-slate-400">
                  Aviso da COFY
                </p>
                <h2 className="text-base font-bold text-white break-words">
                  {currentNotice.title}
                </h2>
              </div>
            </div>

            <div className="p-4">
              <div className="text-sm leading-relaxed text-slate-200 whitespace-pre-wrap break-words">
                {currentNotice.message}
              </div>

              {currentNotice.priority === "important" && (
                <div className="mt-3 inline-flex items-center rounded-full border border-amber-500/20 bg-amber-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-300">
                  Mensagem importante
                </div>
              )}

              <Button
                type="button"
                onClick={handleNoticeRead}
                disabled={noticeConfirming}
                className="mt-5 w-full h-11 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {noticeConfirming ? "Confirmando..." : "Entendi"}
              </Button>

              {userNotices.length > 1 && (
                <p className="mt-2 text-center text-[10px] text-slate-500">
                  Há mais {userNotices.length - 1} aviso(s) aguardando leitura.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-sm sticky top-0 z-50">
        <div className="mx-auto max-w-md px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between relative">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-400 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-slate-900" />
            </div>
            <div className="flex flex-col">
              <h1
                className="text-lg sm:text-xl font-bold text-white leading-tight"
                style={{ fontFamily: "Space Grotesk, sans-serif" }}
              >
                Trader Café NY
              </h1>
              <p className="text-xs text-slate-200">Olá, {user?.name || user?.email}</p>
            </div>
          </div>

          <div className="relative">
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => setShowMenu((prev) => !prev)}
              className="border-slate-700 text-white bg-slate-900 hover:bg-slate-800"
            >
              <Menu className="w-4 h-4" />
            </Button>

            {showMenu && (
              <div className="absolute right-0 mt-2 w-40 rounded-lg bg-slate-900 border border-slate-700 shadow-lg z-50">
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-100 hover:bg-slate-800"
                  onClick={() => {
                    setShowMenu(false);
                    setShowDeposit(true);
                  }}
                >
                  <DollarSign className="w-4 h-4 text-emerald-400" />
                  Depositar
                </button>
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-100 hover:bg-slate-800"
                  onClick={async () => {
                    setShowMenu(false);
                    setProfileError("");
                    await fetchUserProfile();
                    setShowProfileModal(true);
                  }}
                >
                  <UserRound className="w-4 h-4 text-amber-300" />
                  {withdrawProfileCompleted ? "Meu cadastro" : "Completar cadastro"}
                </button>

                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-100 hover:bg-slate-800"
                  onClick={handleOpenWithdrawal}
                >
                  <ArrowDownToLine className="w-4 h-4 text-cyan-400" />
                  Sacar
                </button>
                <button
                  type="button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-400 hover:bg-slate-800"
                  onClick={() => {
                    setShowMenu(false);
                    onLogout();
                  }}
                >
                  <LogOut className="w-4 h-4" />
                  Sair
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="mx-auto max-w-md px-3 sm:px-4 py-5 sm:py-6">
        <div className="space-y-5">
          {/* ===== Conta + Mercado ===== */}
          <Card className="overflow-hidden border-slate-800 bg-slate-900 shadow-2xl shadow-black/20">
            <CardContent className="p-0">
              {/* CONTA */}
              <div className="relative overflow-hidden border-b border-slate-800 px-4 py-5">
                <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-emerald-500/10 blur-3xl" />
                <div className="relative">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-slate-300">
                      <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-emerald-500/20 bg-emerald-500/10">
                        <Wallet className="h-4 w-4 text-emerald-300" />
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Sua conta</p>
                        <p className="text-xs text-slate-300">Saldo disponível</p>
                      </div>
                    </div>
                    <span className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider text-emerald-300">
                      Conta ativa
                    </span>
                  </div>

                  <div className="mt-3">
                    <p className="text-[32px] font-black leading-none tracking-tight text-white sm:text-4xl">
                      {formatBrl(balance)}
                    </p>
                    <p className="mt-2 text-[11px] text-slate-500">
                      Disponível para novas operações e saques.
                    </p>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setShowDeposit(true)}
                      className="flex h-10 items-center justify-center gap-2 rounded-xl bg-emerald-500 font-bold text-slate-950 transition active:scale-[0.98] hover:bg-emerald-400"
                    >
                      <DollarSign className="h-4 w-4" />
                      Depositar
                    </button>
                    <button
                      type="button"
                      onClick={handleOpenWithdrawal}
                      className="flex h-10 items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-800/70 font-bold text-white transition active:scale-[0.98] hover:bg-slate-800"
                    >
                      <ArrowDownToLine className="h-4 w-4" />
                      Sacar
                    </button>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-2">
                    <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">
                      <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">Patrimônio</p>
                      <p className="mt-1 text-sm font-extrabold text-white">{formatBrl(portfolioTotalBrl)}</p>
                    </div>
                    <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">
                      <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">Poder de compra</p>
                      <p className="mt-1 text-sm font-extrabold text-white">
                        {buyPrice > 0 ? `${cashSacksAtBuy.toFixed(2)} sacas` : "--"}
                      </p>
                    </div>
                  </div>

                  {hasPosition && (
                    <div className="mt-2 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-[9px] font-semibold uppercase tracking-wider text-emerald-300">Posição aplicada</p>
                          <p className="mt-1 text-sm font-bold text-white">{positionSacks.toFixed(2)} sacas</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[9px] uppercase tracking-wider text-slate-500">Valor atual</p>
                          <p className="mt-1 text-sm font-bold text-emerald-200">{formatBrl(positionValueBrl)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-2 flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950/30 px-3 py-2.5">
                    <span className="text-[10px] uppercase tracking-wider text-slate-500">Resultado histórico</span>
                    <span className={`text-sm font-extrabold ${totalProfit >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                      {totalProfit >= 0 ? "+" : ""}{formatBrl(totalProfit)}
                    </span>
                  </div>
                </div>
              </div>

              {/* MERCADO */}
              <div className="px-4 py-4">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Mercado agora</p>
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[8px] font-black uppercase tracking-wider ${quoteIsLive ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-300" : "border-amber-500/25 bg-amber-500/10 text-amber-300"}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${quoteIsLive ? "animate-pulse bg-emerald-400" : "bg-amber-400"}`} />
                        {quoteIsLive ? "Mercado ativo" : "Sem atualização"}
                      </span>
                    </div>
                    <h2 className="mt-0.5 text-sm font-bold text-white">Café NY • {selectedKcSymbol}</h2>
                    <p className="mt-0.5 text-[9px] text-slate-600">Última atualização: {quoteTimeLabel}</p>
                  </div>
                  <div
                    className={[
                      "flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-extrabold transition-all duration-300",
                      kcChangePct > 0
                        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                        : kcChangePct < 0
                        ? "border-red-500/30 bg-red-500/10 text-red-300"
                        : "border-slate-700 bg-slate-800 text-slate-300",
                    ].join(" ")}
                  >
                    {kcChangePct > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : kcChangePct < 0 ? <TrendingDown className="h-3.5 w-3.5" /> : null}
                    {kcChangePct > 0 ? "+" : ""}{kcChangePct.toFixed(2)}%
                  </div>
                </div>

                <div
                  className={[
                    "rounded-2xl border p-4 transition-all duration-500",
                    kcFlash === "up"
                      ? "scale-[1.01] border-emerald-500/40 bg-emerald-500/10 shadow-lg shadow-emerald-500/10"
                      : kcFlash === "down"
                      ? "scale-[1.01] border-red-500/40 bg-red-500/10 shadow-lg shadow-red-500/10"
                      : "border-slate-800 bg-slate-950/45",
                  ].join(" ")}
                >
                  <div className="flex items-end justify-between gap-3">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500">Último</p>
                      <div className="mt-0.5 flex items-baseline gap-1.5">
                        <span className="text-3xl font-black tracking-tight text-white">
                          {formatQuote(kcDisplay, 2)}
                        </span>
                        <span className="text-xs font-bold text-slate-400">¢/lb</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[9px] uppercase tracking-wider text-slate-500">Variação</p>
                      <p className={`mt-0.5 text-sm font-bold ${kcChangeAbs > 0 ? "text-emerald-300" : kcChangeAbs < 0 ? "text-red-300" : "text-slate-300"}`}>
                        {kcChangeAbs > 0 ? "+" : ""}{kcChangeAbs.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 border-t border-slate-800 pt-3 text-[11px]">
                    <div className="flex items-center justify-between gap-2"><span className="text-slate-500">Abertura</span><strong className="text-slate-200">{formatQuote(kcData?.open, 2)}</strong></div>
                    <div className="flex items-center justify-between gap-2"><span className="text-slate-500">Máxima</span><strong className="text-emerald-300">{formatQuote(kcData?.high, 2)}</strong></div>
                    <div className="flex items-center justify-between gap-2"><span className="text-slate-500">Mínima</span><strong className="text-red-300">{formatQuote(kcData?.low, 2)}</strong></div>
                    <div className="flex items-center justify-between gap-2"><span className="text-slate-500">Fechamento</span><strong className="text-slate-200">{formatQuote(kcData?.close, 2)}</strong></div>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="rounded-xl border border-emerald-500/15 bg-emerald-500/5 px-3 py-2">
                      <p className="text-[9px] uppercase tracking-wider text-slate-500">Bid</p>
                      <p className="mt-0.5 text-sm font-bold text-emerald-200">{formatQuote(kcData?.bid, 2)}</p>
                    </div>
                    <div className="rounded-xl border border-red-500/15 bg-red-500/5 px-3 py-2 text-right">
                      <p className="text-[9px] uppercase tracking-wider text-slate-500">Ask</p>
                      <p className="mt-0.5 text-sm font-bold text-red-200">{formatQuote(kcData?.ask, 2)}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div
                    className={[
                      "rounded-xl border p-3 transition-all duration-300",
                      usdFlash === "up"
                        ? "border-emerald-500/30 bg-emerald-500/10"
                        : usdFlash === "down"
                        ? "border-red-500/30 bg-red-500/10"
                        : "border-slate-800 bg-slate-950/40",
                    ].join(" ")}
                  >
                    <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">Dólar</p>
                    <p className="mt-1 text-sm font-extrabold text-cyan-200">
                      {effectiveUsdBrl > 0 ? `R$ ${formatQuote(effectiveUsdBrl, 4)}` : "--"}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
                    <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">Saca referência</p>
                    <p className="mt-1 text-sm font-extrabold text-white">{fairPrice > 0 ? formatBrl(fairPrice) : "--"}</p>
                  </div>
                </div>

                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => { setTradeSide("buy"); setTradeError(""); }}
                    className="group rounded-2xl border border-emerald-500/25 bg-gradient-to-b from-emerald-500/15 to-emerald-500/5 p-3 text-left transition duration-200 hover:border-emerald-400/50 active:scale-[0.98]"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-400 text-slate-950 transition group-active:-translate-y-0.5">
                        <TrendingUp className="h-4 w-4" />
                      </div>
                      <span className="text-[9px] font-bold uppercase tracking-wider text-emerald-300">Alta</span>
                    </div>
                    <p className="mt-3 text-xs font-bold text-emerald-200">COMPRAR</p>
                    <p className="mt-0.5 text-base font-black text-white">{buyPrice > 0 ? formatBrl(buyPrice) : "--"}</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => { setTradeSide("sell"); setTradeError(""); }}
                    className="group rounded-2xl border border-red-500/25 bg-gradient-to-b from-red-500/15 to-red-500/5 p-3 text-left transition duration-200 hover:border-red-400/50 active:scale-[0.98]"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-red-400 text-slate-950 transition group-active:translate-y-0.5">
                        <TrendingDown className="h-4 w-4" />
                      </div>
                      <span className="text-[9px] font-bold uppercase tracking-wider text-red-300">Baixa</span>
                    </div>
                    <p className="mt-3 text-xs font-bold text-red-200">VENDER</p>
                    <p className="mt-0.5 text-base font-black text-white">{sellPrice > 0 ? formatBrl(sellPrice) : "--"}</p>
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ===== Abrir operação ===== */}
          <Card className="overflow-hidden border-slate-800 bg-slate-900 shadow-2xl shadow-black/20">
            <CardHeader className="border-b border-slate-800 pb-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-500">Nova ordem</p>
                  <CardTitle className="mt-1 text-base text-white">Abrir operação</CardTitle>
                </div>
                <span className={`rounded-full border px-2.5 py-1 text-[9px] font-bold uppercase tracking-wider ${tradeSide === "buy" ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-300" : "border-red-500/20 bg-red-500/10 text-red-300"}`}>
                  {tradeSide === "buy" ? "Alta selecionada" : "Baixa selecionada"}
                </span>
              </div>
            </CardHeader>

            <CardContent className="space-y-4 pt-4">
              <div className="grid grid-cols-2 gap-2 rounded-2xl bg-slate-950/50 p-1.5">
                <button
                  type="button"
                  onClick={() => { setTradeSide("buy"); setTradeError(""); }}
                  className={[
                    "flex min-h-[58px] items-center gap-2 rounded-xl px-3 text-left transition-all duration-200",
                    tradeSide === "buy"
                      ? "bg-emerald-500 text-slate-950 shadow-lg shadow-emerald-500/20"
                      : "text-slate-400 hover:bg-slate-800/80",
                  ].join(" ")}
                >
                  <TrendingUp className="h-5 w-5 shrink-0" />
                  <div>
                    <p className="text-xs font-black">COMPRAR</p>
                    <p className={`text-[9px] ${tradeSide === "buy" ? "text-slate-900/70" : "text-slate-500"}`}>Lucrar com alta</p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => { setTradeSide("sell"); setTradeError(""); }}
                  className={[
                    "flex min-h-[58px] items-center gap-2 rounded-xl px-3 text-left transition-all duration-200",
                    tradeSide === "sell"
                      ? "bg-red-500 text-white shadow-lg shadow-red-500/20"
                      : "text-slate-400 hover:bg-slate-800/80",
                  ].join(" ")}
                >
                  <TrendingDown className="h-5 w-5 shrink-0" />
                  <div>
                    <p className="text-xs font-black">VENDER</p>
                    <p className={`text-[9px] ${tradeSide === "sell" ? "text-white/70" : "text-slate-500"}`}>Lucrar com baixa</p>
                  </div>
                </button>
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <label className="text-xs font-semibold text-slate-300">Valor da operação</label>
                  <span className="text-[10px] text-slate-500">Saldo: {formatBrl(balance)}</span>
                </div>

                <div className={`rounded-2xl border bg-slate-950 px-4 py-3 transition ${tradeSide === "buy" ? "border-emerald-500/30 focus-within:border-emerald-400/70" : "border-red-500/30 focus-within:border-red-400/70"}`}>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={tradeAmountDisplay}
                    onChange={(e) => handleTradeAmountMasked(e.target.value)}
                    placeholder="R$ 0,00"
                    className="w-full bg-transparent text-2xl font-black tracking-tight text-white outline-none placeholder:text-slate-700"
                  />
                  <p className="mt-1 text-[10px] text-slate-500">Digite o valor que deseja movimentar.</p>
                </div>

                <div className="mt-2 grid grid-cols-5 gap-1.5">
                  {[100, 500, 1000, 5000].map((value) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setQuickTradeAmount(value)}
                      className="rounded-lg border border-slate-800 bg-slate-950/60 px-1 py-2 text-[9px] font-bold text-slate-300 transition hover:border-slate-600 active:scale-95"
                    >
                      {value >= 1000 ? `${value / 1000} mil` : value}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setQuickTradeAmount(balance)}
                    className="rounded-lg border border-slate-700 bg-slate-800 px-1 py-2 text-[9px] font-black text-white transition hover:bg-slate-700 active:scale-95"
                  >
                    MÁX.
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500">Preço de entrada</p>
                  <p className={`mt-1 text-sm font-extrabold ${tradeSide === "buy" ? "text-emerald-200" : "text-red-200"}`}>
                    {selectedEntryPrice > 0 ? formatBrl(selectedEntryPrice) : "--"}
                  </p>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-3">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500">Estimativa</p>
                  <p className="mt-1 text-sm font-extrabold text-white">
                    {selectedEstimatedQty > 0 ? `${selectedEstimatedQty.toFixed(4)} saca(s)` : "--"}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-950/30 px-3 py-2.5">
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-slate-500">Spread da plataforma</p>
                  <p className="mt-0.5 text-xs font-bold text-slate-200">{spreadPct.toFixed(2)}%</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500">Direção</p>
                  <div className={`mt-0.5 flex items-center justify-end gap-1 text-xs font-bold ${tradeSide === "buy" ? "text-emerald-300" : "text-red-300"}`}>
                    {tradeSide === "buy" ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                    {tradeSide === "buy" ? "Alta" : "Baixa"}
                  </div>
                </div>
              </div>

              {fairPrice <= 0 && (
                <p className="rounded-xl border border-amber-500/20 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-300">
                  Aguardando cotação da bolsa + dólar para calcular o preço da saca.
                </p>
              )}

              {tradeError && (
                <p className="rounded-xl border border-red-500/20 bg-red-500/5 px-3 py-2 text-[11px] text-red-300">
                  {tradeError}
                </p>
              )}

              {tradeSide === "buy" ? (
                <Button
                  type="button"
                  disabled={buyLoading || fairPrice <= 0 || tradeAmountNum <= 0}
                  onClick={() => requestTradeConfirmation("buy")}
                  className="h-14 w-full rounded-2xl bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-400 text-slate-950 shadow-xl shadow-emerald-500/20 transition hover:-translate-y-0.5 hover:shadow-emerald-500/30 active:translate-y-0 disabled:pointer-events-none disabled:opacity-50"
                >
                  <span className="flex items-center justify-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    <span className="font-black tracking-wide">
                      {buyLoading ? "ABRINDO COMPRA..." : `COMPRAR • ${tradeAmountNum > 0 ? formatBrl(tradeAmountNum) : "ALTA"}`}
                    </span>
                  </span>
                </Button>
              ) : (
                <Button
                  type="button"
                  disabled={sellLoading || fairPrice <= 0 || tradeAmountNum <= 0}
                  onClick={() => requestTradeConfirmation("sell")}
                  className="h-14 w-full rounded-2xl bg-gradient-to-r from-red-400 via-red-500 to-red-400 text-white shadow-xl shadow-red-500/20 transition hover:-translate-y-0.5 hover:shadow-red-500/30 active:translate-y-0 disabled:pointer-events-none disabled:opacity-50"
                >
                  <span className="flex items-center justify-center gap-2">
                    <TrendingDown className="h-5 w-5" />
                    <span className="font-black tracking-wide">
                      {sellLoading ? "ABRINDO VENDA..." : `VENDER • ${tradeAmountNum > 0 ? formatBrl(tradeAmountNum) : "BAIXA"}`}
                    </span>
                  </span>
                </Button>
              )}

              <p className="text-center text-[9px] leading-relaxed text-slate-600">
                Confira direção, valor e preço de entrada antes de confirmar a operação.
              </p>
            </CardContent>
          </Card>

          {/* ===== Minha Parceria - botão compacto mobile ===== */}
          {partnerData?.is_partner === true && (
            <button
              type="button"
              onClick={openPartnerPanel}
              className="relative w-full rounded-2xl border border-emerald-500/25 bg-slate-900 p-3 text-left shadow-lg transition active:scale-[0.99]"
            >
              <div className="flex min-w-0 items-center gap-3">
                <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-300">
                  <DollarSign className="h-5 w-5" />
                  {newPartnerCommissions > 0 && (
                    <span className="absolute -right-1.5 -top-1.5 flex min-h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-black text-white ring-2 ring-slate-900">
                      {newPartnerCommissions > 99 ? "99+" : newPartnerCommissions}
                    </span>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="truncate text-sm font-black text-white">Minha Parceria</p>
                    {newPartnerCommissions > 0 && (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-red-300">
                        <BellRing className="h-3 w-3" />
                        Nova comissão
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-slate-400">
                    <strong className="text-emerald-300">{formatBrl(partnerAvailable)}</strong> disponível
                    <span className="mx-1.5 text-slate-700">•</span>
                    {partnerReferralCount} indicado{partnerReferralCount === 1 ? "" : "s"}
                  </p>
                </div>

                <span className="shrink-0 text-xl text-slate-500">›</span>
              </div>
            </button>
          )}

          {/* Histórico */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="pb-2 sm:pb-3">
              <CardTitle className="text-base sm:text-lg text-white">Histórico de Operações</CardTitle>
            </CardHeader>
            <CardContent className="pt-1 sm:pt-2">
              <Tabs defaultValue="open">
                <TabsList className="w-full bg-slate-800 mb-3 flex">
                  <TabsTrigger value="open" className="flex-1 text-xs sm:text-sm text-white">
                    Abertas ({openOrders.length})
                  </TabsTrigger>
                  <TabsTrigger value="closed" className="flex-1 text-xs sm:text-sm text-white">
                    Fechadas ({closedOrders.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="open">
                  <div className="overflow-x-auto -mx-2 sm:mx-0">
                    <OrdersTable
                      orders={openOrders}
                      marketData={marketDataForOrders}
                      onOrderClosed={handleOrderClosed}
                      userSettings={userSettingsNoMinClose}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="closed">
                  <div className="overflow-x-auto -mx-2 sm:mx-0">
                    <OrdersTable orders={closedOrders} userSettings={userSettingsNoMinClose} />
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>


      {/* ===== Painel Minha Parceria ===== */}
      {showPartnerPanel && partnerData?.is_partner === true && (
        <div
          className="fixed inset-0 z-[160] flex items-end justify-center bg-black/80 p-0 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={() => setShowPartnerPanel(false)}
        >
          <div
            className="max-h-[92%] w-full overflow-y-auto rounded-t-3xl border border-slate-800 bg-slate-950 shadow-2xl sm:max-w-md sm:rounded-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-800 bg-slate-950/95 px-4 py-4 backdrop-blur">
              <div>
                <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-emerald-400">
                  Programa de parceiros
                </p>
                <h3 className="mt-1 text-xl font-black text-white">Minha Parceria</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowPartnerPanel(false)}
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-800 bg-slate-900 text-slate-300"
                aria-label="Fechar Minha Parceria"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 p-4">
              <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Comissão disponível
                </p>
                <p className="mt-1 text-3xl font-black text-emerald-300">
                  {formatBrl(partnerAvailable)}
                </p>
                <div className="mt-3 flex items-center justify-between gap-2 text-xs">
                  <span className="text-slate-400">Sua participação</span>
                  <strong className="rounded-lg bg-slate-950 px-2.5 py-1.5 text-emerald-300">
                    {partnerCommissionPct.toFixed(2)}% do spread
                  </strong>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-xl border border-slate-800 bg-slate-900 p-3">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500">Indicados</p>
                  <p className="mt-1 text-xl font-black text-white">{partnerReferralCount}</p>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900 p-3">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500">Total comissões</p>
                  <p className="mt-1 text-base font-black text-white">{formatBrl(partnerTotalCommission)}</p>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900 p-3">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500">Spread gerado</p>
                  <p className="mt-1 text-base font-black text-white">{formatBrl(partnerSpreadGenerated)}</p>
                </div>
                <div className="rounded-xl border border-slate-800 bg-slate-900 p-3">
                  <p className="text-[9px] uppercase tracking-wider text-slate-500">Pago</p>
                  <p className="mt-1 text-base font-black text-white">{formatBrl(partnerPaid)}</p>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Indicação
                </p>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] text-slate-500">Código atual</p>
                    <strong className="block truncate text-lg tracking-wider text-white">
                      {partnerCode || "--"}
                    </strong>
                  </div>
                  <button
                    type="button"
                    onClick={handleShareReferral}
                    disabled={!partnerReferralLink}
                    className="shrink-0 rounded-xl bg-emerald-500 px-4 py-3 text-xs font-black text-slate-950 disabled:opacity-50"
                  >
                    Compartilhar
                  </button>
                </div>
                {partnerReferralLink && (
                  <div className="mt-3 rounded-xl border border-slate-800 bg-slate-950 px-3 py-2.5">
                    <p className="text-[9px] uppercase tracking-wider text-slate-600">Seu link</p>
                    <p className="mt-1 break-all text-[11px] font-semibold text-cyan-300">
                      {partnerReferralLink}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Últimas comissões
                  </p>
                  <span className="text-[10px] text-slate-600">máx. 10</span>
                </div>

                {partnerCommissions.length > 0 ? (
                  <div className="space-y-2">
                    {partnerCommissions.slice(0, 10).map((item, index) => (
                      <div
                        key={item?.id ?? index}
                        className="flex items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-900 px-3 py-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-xs font-bold text-white">
                            {String(item?.event_type || "").toLowerCase() === "close"
                              ? "Fechamento"
                              : "Abertura"}{" "}
                            da operação #{item?.order_id ?? "--"}
                          </p>
                          <p className="mt-0.5 truncate text-[9px] text-slate-500">
                            {item?.referred_name || `Usuário #${item?.referred_user_id ?? "--"}`}
                            {item?.created_at ? ` • ${item.created_at}` : ""}
                          </p>
                        </div>
                        <strong className="shrink-0 text-sm text-emerald-300">
                          +{formatBrl(item?.commission_amount ?? 0)}
                        </strong>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-slate-800 bg-slate-900 p-4 text-center text-xs text-slate-500">
                    Nenhuma comissão registrada ainda.
                  </div>
                )}
              </div>

              {partnerLoading && (
                <p className="text-center text-xs text-slate-500">Atualizando parceria...</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ===== Confirmação antes de abrir operação ===== */}
      {pendingTradeConfirm && (
        <div className="fixed inset-0 z-[145] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm" onClick={() => !buyLoading && !sellLoading && setPendingTradeConfirm(null)}>
          <div className={`w-full max-w-sm overflow-hidden rounded-3xl border bg-slate-950 shadow-2xl ${pendingTradeConfirm.side === "buy" ? "border-emerald-500/30" : "border-red-500/30"}`} onClick={(e) => e.stopPropagation()}>
            <div className={`p-5 text-center ${pendingTradeConfirm.side === "buy" ? "bg-emerald-500/10" : "bg-red-500/10"}`}>
              <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full ${pendingTradeConfirm.side === "buy" ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`}>
                {pendingTradeConfirm.side === "buy" ? <TrendingUp className="h-7 w-7" /> : <TrendingDown className="h-7 w-7" />}
              </div>
              <h3 className="mt-3 text-xl font-black text-white">Confirmar operação</h3>
              <p className={`mt-1 text-sm font-bold ${pendingTradeConfirm.side === "buy" ? "text-emerald-300" : "text-red-300"}`}>{pendingTradeConfirm.side === "buy" ? "COMPRA • ALTA" : "VENDA • BAIXA"}</p>
            </div>
            <div className="p-5">
              <div className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
                <div className="flex items-center justify-between gap-3"><span className="text-xs text-slate-500">Valor da operação</span><strong className="text-lg text-white">{formatBrl(pendingTradeConfirm.amount)}</strong></div>
                <div className="mt-3 flex items-center justify-between gap-3"><span className="text-xs text-slate-500">Preço aproximado de entrada</span><strong className="text-white">{formatBrl(pendingTradeConfirm.side === "buy" ? buyPrice : sellPrice)}</strong></div>
                <div className="mt-3 flex items-center justify-between gap-3"><span className="text-xs text-slate-500">Quantidade estimada</span><strong className="text-white">{(pendingTradeConfirm.side === "buy" ? estimatedBuyCofy : estimatedSellCofy).toFixed(4)} saca(s)</strong></div>
                <div className="mt-3 flex items-center justify-between gap-3"><span className="text-xs text-slate-500">Spread</span><strong className="text-white">{spreadPct.toFixed(2)}%</strong></div>
              </div>
              <Button type="button" onClick={() => { const side = pendingTradeConfirm.side; setPendingTradeConfirm(null); if (side === "buy") handleBuy(); else handleSell(); }} disabled={buyLoading || sellLoading} className={`mt-4 h-14 w-full rounded-2xl text-base font-black ${pendingTradeConfirm.side === "buy" ? "bg-emerald-500 text-slate-950 hover:bg-emerald-400" : "bg-red-500 text-white hover:bg-red-600"}`}>{pendingTradeConfirm.side === "buy" ? "CONFIRMAR COMPRA" : "CONFIRMAR VENDA"}</Button>
              <Button type="button" onClick={() => setPendingTradeConfirm(null)} disabled={buyLoading || sellLoading} className="mt-2 h-14 w-full rounded-2xl bg-slate-800 text-base font-black text-white hover:bg-slate-700">CANCELAR</Button>
              <p className="mt-3 text-center text-[10px] leading-relaxed text-slate-500">A operação só será enviada após a confirmação acima.</p>
            </div>
          </div>
        </div>
      )}

      {/* ===== Cadastro para saque ===== */}
      {showProfileModal && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 p-3">
          <div className="w-full max-w-md max-h-[92vh] overflow-y-auto rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-800 bg-slate-950 px-4 py-3">
              <div>
                <p className="text-sm font-bold text-white">
                  {withdrawProfileCompleted ? "Meu cadastro" : "Completar cadastro para saque"}
                </p>
                <p className="mt-0.5 text-[10px] text-slate-400">
                  Estes dados são usados para liberar e facilitar seus saques via PIX.
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowProfileModal(false);
                  setProfileError("");
                }}
                className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-800 hover:text-white"
                aria-label="Fechar cadastro"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleSaveProfile} className="space-y-4 p-4">
              {profileLoading && (
                <p className="text-xs text-slate-400">Carregando cadastro...</p>
              )}

              <div className="space-y-1">
                <label className="text-xs text-slate-300">Nome completo</label>
                <input
                  type="text"
                  value={profileData.name}
                  onChange={(e) =>
                    setProfileData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs text-slate-300">E-mail</label>
                <input
                  type="email"
                  value={profileData.email}
                  disabled
                  className="w-full cursor-not-allowed rounded-lg border border-slate-800 bg-slate-900/60 px-3 py-2.5 text-sm text-slate-400"
                />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-xs text-slate-300">CPF</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={formatCpfInput(profileData.cpf)}
                    onChange={(e) =>
                      setProfileData((prev) => ({
                        ...prev,
                        cpf: e.target.value.replace(/\D/g, "").slice(0, 11),
                        ...(prev.pix_key_type === "cpf"
                          ? { pix_key: e.target.value.replace(/\D/g, "").slice(0, 11) }
                          : {}),
                      }))
                    }
                    placeholder="000.000.000-00"
                    required
                    className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-slate-300">Telefone / WhatsApp</label>
                  <input
                    type="text"
                    inputMode="tel"
                    value={formatPhoneInput(profileData.phone)}
                    onChange={(e) =>
                      setProfileData((prev) => ({
                        ...prev,
                        phone: e.target.value.replace(/\D/g, "").slice(0, 11),
                      }))
                    }
                    placeholder="(00) 00000-0000"
                    required
                    className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs text-slate-300">Data de nascimento</label>
                <input
                  type="date"
                  value={profileData.birth_date}
                  onChange={(e) =>
                    setProfileData((prev) => ({
                      ...prev,
                      birth_date: e.target.value,
                    }))
                  }
                  required
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500"
                />
              </div>

              <div className="grid grid-cols-[1fr_92px] gap-3">
                <div className="space-y-1">
                  <label className="text-xs text-slate-300">Cidade</label>
                  <input
                    type="text"
                    value={profileData.city}
                    onChange={(e) =>
                      setProfileData((prev) => ({
                        ...prev,
                        city: e.target.value,
                      }))
                    }
                    required
                    className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-slate-300">UF</label>
                  <input
                    type="text"
                    maxLength={2}
                    value={profileData.state}
                    onChange={(e) =>
                      setProfileData((prev) => ({
                        ...prev,
                        state: e.target.value
                          .replace(/[^a-zA-Z]/g, "")
                          .slice(0, 2)
                          .toUpperCase(),
                      }))
                    }
                    placeholder="MG"
                    required
                    className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm uppercase text-white outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs text-slate-300">Tipo da chave PIX</label>
                <select
                  value={profileData.pix_key_type}
                  onChange={(e) =>
                    setProfileData((prev) => ({
                      ...prev,
                      pix_key_type: e.target.value,
                      pix_key:
                        e.target.value === "cpf" ? prev.cpf : "",
                    }))
                  }
                  className="w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-sm text-white outline-none focus:border-emerald-500"
                >
                  <option value="cpf">CPF</option>
                  <option value="email">E-mail</option>
                  <option value="phone">Telefone</option>
                  <option value="random">Chave aleatória</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="text-xs text-slate-300">Chave PIX</label>
                <input
                  type={
                    profileData.pix_key_type === "email" ? "email" : "text"
                  }
                  value={
                    profileData.pix_key_type === "cpf"
                      ? formatCpfInput(profileData.cpf)
                      : profileData.pix_key
                  }
                  disabled={profileData.pix_key_type === "cpf"}
                  onChange={(e) =>
                    setProfileData((prev) => ({
                      ...prev,
                      pix_key: e.target.value,
                    }))
                  }
                  placeholder={
                    profileData.pix_key_type === "email"
                      ? "seu@email.com"
                      : profileData.pix_key_type === "phone"
                      ? "Telefone cadastrado no PIX"
                      : profileData.pix_key_type === "random"
                      ? "Cole sua chave aleatória"
                      : "CPF do cadastro"
                  }
                  required
                  className={[
                    "w-full rounded-lg border px-3 py-2.5 text-sm outline-none",
                    profileData.pix_key_type === "cpf"
                      ? "cursor-not-allowed border-slate-800 bg-slate-900/60 text-slate-400"
                      : "border-slate-700 bg-slate-900 text-white focus:border-emerald-500",
                  ].join(" ")}
                />
              </div>

              <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
                <p className="text-[11px] leading-relaxed text-amber-100/80">
                  Confira sua chave PIX antes de salvar. Ela será usada para facilitar os próximos saques.
                </p>
              </div>

              {profileError && (
                <p className="rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2 text-xs text-red-300">
                  {profileError}
                </p>
              )}

              <Button
                type="submit"
                disabled={profileSaving || profileLoading}
                className="w-full h-11 rounded-xl bg-gradient-to-r from-emerald-400 to-cyan-400 font-bold text-slate-950"
              >
                {profileSaving
                  ? "Salvando..."
                  : withdrawProfileCompleted
                  ? "Salvar alterações"
                  : "Completar cadastro e continuar"}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* ===== Assistente COFY / Suporte em tela cheia ===== */}
      {showAssistant && (
        <div className="fixed inset-0 z-[150] flex h-[100dvh] w-full flex-col bg-slate-950 text-white">
          {/* Cabeçalho grande e fácil de fechar */}
          <div className="shrink-0 border-b border-slate-800 bg-slate-900/95 px-4 pb-3 pt-[max(12px,env(safe-area-inset-top))] shadow-lg backdrop-blur">
            <div className="mx-auto flex w-full max-w-3xl items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-400 to-cyan-400 shadow-lg shadow-emerald-500/20">
                  <Bot className="h-6 w-6 text-slate-950" />
                  <span className="absolute -right-1 -top-1 h-3.5 w-3.5 rounded-full border-2 border-slate-900 bg-emerald-400" />
                </div>

                <div className="min-w-0">
                  <p className="truncate text-lg font-extrabold tracking-tight text-white">
                    Ajuda
                  </p>
                  <div className="mt-0.5 flex items-center gap-1.5 text-xs font-medium text-emerald-300">
                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                    Assistente disponível para ajudar
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowAssistant(false)}
                className="flex h-12 shrink-0 items-center gap-2 rounded-xl border border-slate-700 bg-slate-800 px-3.5 text-sm font-bold text-white shadow-md transition active:scale-95 hover:bg-slate-700"
                aria-label="Fechar conversa"
                title="Fechar conversa"
              >
                <X className="h-5 w-5" />
                <span className="hidden min-[360px]:inline">Fechar</span>
              </button>
            </div>
          </div>

          {/* Área principal da conversa */}
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-slate-950">
            <div className="mx-auto flex min-h-full w-full max-w-3xl flex-col px-3 py-4 sm:px-5">
              <div className="mb-4 rounded-2xl border border-emerald-500/15 bg-emerald-500/5 px-4 py-3">
                <p className="text-sm leading-relaxed text-slate-200">
                  Precisa de ajuda com o Trader? Escreva sua dúvida abaixo. Posso explicar operações,
                  saldo, depósito, saque, cotações e o funcionamento da plataforma.
                </p>
              </div>

              {assistantMessages.length <= 1 && !assistantLoading && (
                <div className="mb-4">
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">Dúvidas rápidas</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ["Como funciona Comprar?", "Comprar / Alta"],
                      ["Como funciona Vender?", "Vender / Baixa"],
                      ["Como faço um depósito?", "Depositar"],
                      ["Como faço para sacar?", "Sacar"],
                      ["Por que uma ordem pode ficar no prejuízo?", "Lucro / Prejuízo"],
                      ["Como encerrar uma ordem?", "Encerrar ordem"],
                    ].map(([question, label]) => (
                      <button key={label} type="button" onClick={() => handleAssistantSend(question)} className="min-h-[48px] rounded-xl border border-slate-800 bg-slate-900 px-3 py-2 text-left text-xs font-bold text-slate-200 transition hover:border-emerald-500/30 hover:bg-emerald-500/5 active:scale-[0.98]">
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {assistantMessages.map((item, index) => (
                  <div
                    key={`${item.role}-${index}`}
                    className={`flex ${item.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={[
                        "max-w-[90%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-[15px] leading-6 shadow-sm sm:max-w-[82%] sm:text-base",
                        item.role === "user"
                          ? "rounded-br-md bg-emerald-500 font-medium text-slate-950 shadow-emerald-500/10"
                          : "rounded-bl-md border border-slate-800 bg-slate-900 text-slate-100 shadow-black/20",
                      ].join(" ")}
                    >
                      {item.content}
                    </div>
                  </div>
                ))}

                {assistantLoading && (
                  <div className="flex justify-start">
                    <div className="flex items-center gap-2 rounded-2xl rounded-bl-md border border-slate-800 bg-slate-900 px-4 py-3 text-sm text-slate-300">
                      <span className="h-2 w-2 animate-bounce rounded-full bg-emerald-400 [animation-delay:-0.20s]" />
                      <span className="h-2 w-2 animate-bounce rounded-full bg-emerald-400 [animation-delay:-0.10s]" />
                      <span className="h-2 w-2 animate-bounce rounded-full bg-emerald-400" />
                      <span className="ml-1">Preparando resposta...</span>
                    </div>
                  </div>
                )}

                <div ref={assistantEndRef} className="h-1" />
              </div>
            </div>
          </div>

          {/* Campo de mensagem fixo e grande */}
          <div className="shrink-0 border-t border-slate-800 bg-slate-900/95 px-3 pb-[max(12px,env(safe-area-inset-bottom))] pt-3 shadow-[0_-10px_30px_rgba(0,0,0,0.22)] backdrop-blur sm:px-5">
            <div className="mx-auto w-full max-w-3xl">
              {assistantError && (
                <p className="mb-2 rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-2 text-sm text-red-300">
                  {assistantError}
                </p>
              )}

              <div className="flex items-end gap-2.5">
                <textarea
                  rows={1}
                  value={assistantMessage}
                  onChange={(e) => {
                    setAssistantMessage(e.target.value);
                    setAssistantError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleAssistantSend();
                    }
                  }}
                  placeholder="Digite aqui como podemos ajudar..."
                  className="max-h-36 min-h-[56px] flex-1 resize-none rounded-2xl border border-slate-700 bg-slate-950 px-4 py-4 text-base leading-6 text-white shadow-inner outline-none placeholder:text-slate-500 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20"
                />

                <button
                  type="button"
                  onClick={() => handleAssistantSend()}
                  disabled={assistantLoading || !String(assistantMessage || "").trim()}
                  className="group flex h-14 min-w-[58px] shrink-0 items-center justify-center gap-2 rounded-2xl bg-gradient-to-br from-emerald-400 to-cyan-400 px-4 font-extrabold text-slate-950 shadow-lg shadow-emerald-500/20 transition hover:scale-[1.02] active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
                  aria-label="Enviar mensagem"
                  title="Enviar mensagem"
                >
                  <Send className="h-5 w-5 transition-transform group-hover:translate-x-0.5" />
                  <span className="hidden sm:inline">Enviar</span>
                </button>
              </div>

              <p className="mt-2 text-center text-[10px] leading-4 text-slate-500 sm:text-xs">
                Suporte informativo por IA. Nenhuma operação é executada automaticamente.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Botão compacto de ajuda quando o chat está fechado */}
      {!showAssistant && (
        <button
          type="button"
          onClick={() => setShowAssistant(true)}
          className="fixed bottom-4 right-4 z-[91] flex h-12 items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500 px-4 text-sm font-black text-slate-950 shadow-xl shadow-emerald-500/20 transition hover:bg-emerald-400 hover:scale-105 active:scale-95"
          aria-label="Abrir ajuda e suporte COFY"
          title="Ajuda"
        >
          <MessageCircle className="h-5 w-5" />
          <span>Ajuda</span>
        </button>
      )}

      <DepositModal open={showDeposit} onClose={() => setShowDeposit(false)} onSuccess={handleDepositSuccess} />
      <WithdrawalModal
        open={showWithdrawal}
        onClose={() => setShowWithdrawal(false)}
        onSuccess={handleWithdrawalSuccess}
        balance={balance}
      />
    </div>
  );
}